import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Shield, FileText, CheckSquare, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useLanguage, API } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const PrivacyConsentPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyUrl, setPrivacyUrl] = useState('https://www.smart-info.it/informativa-privacy-Telegrameapp');
  const [termsUrl, setTermsUrl] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      if (response.data.privacy_url) {
        setPrivacyUrl(response.data.privacy_url);
      }
      if (response.data.terms_url) {
        setTermsUrl(response.data.terms_url);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  const content = {
    it: {
      title: 'Informativa Privacy e Termini',
      intro: 'Prima di continuare, ti informiamo che l\'utilizzo di questo servizio comporta il trattamento dei tuoi dati personali.',
      download: 'Leggi l\'informativa completa',
      downloadSub: 'Clicca qui per aprire la pagina web',
      termsTitle: 'Termini e Condizioni di Servizio',
      termsSub: 'Clicca qui per leggere i termini',
      consent: 'Continuando, dichiari di aver letto e compreso l\'informativa e acconsenti esplicitamente al trattamento dei tuoi dati personali, inclusa la possibilità di trasferimento verso Paesi terzi, ai sensi del GDPR.',
      checkboxPrivacy: 'Ho letto e compreso l\'informativa privacy e acconsento al trattamento dei miei dati personali come descritto',
      checkboxTerms: 'Ho letto e accetto i Termini e le Condizioni di Servizio Smartphone Security',
      button: 'Accetto e Continua'
    },
    en: {
      title: 'Privacy Policy and Terms',
      intro: 'Before continuing, we inform you that the use of this service involves the processing of your personal data.',
      download: 'Read the complete privacy policy',
      downloadSub: 'Click here to open the web page',
      termsTitle: 'Terms and Conditions of Service',
      termsSub: 'Click here to read the terms',
      consent: 'By continuing, you declare that you have read and understood the privacy policy and explicitly consent to the processing of your personal data, including the possibility of transfer to third countries, in accordance with GDPR.',
      checkboxPrivacy: 'I have read and understood the privacy policy and consent to the processing of my personal data as described',
      checkboxTerms: 'I have read and accept the Smartphone Security Terms and Conditions of Service',
      button: 'Accept and Continue'
    }
  };

  const t = content[language];

  const handleContinue = () => {
    if (privacyAccepted && termsAccepted) {
      localStorage.setItem('privacy_accepted', 'true');
      localStorage.setItem('terms_accepted', 'true');
      navigate('/device-selection');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="max-w-3xl w-full">
        <div className="text-center mb-8">
          <img 
            src="/logo.png" 
            alt="Smart Info" 
            className="h-72 md:h-80 lg:h-96 mx-auto mb-8 animate-in fade-in zoom-in duration-1000"
            data-testid="company-logo"
          />
          <Shield className="w-16 h-16 mx-auto mb-4 text-primary animate-pulse" />
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2">
            {t.title}
          </h1>
        </div>

        <div className="glass-panel rounded-sm p-8 md:p-12">
          <div className="space-y-6 mb-8">
            <p className="text-lg leading-relaxed text-foreground">
              {t.intro}
            </p>

            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="privacy-policy-link"
              className="flex items-center gap-3 w-full p-4 bg-primary/10 border border-primary/30 rounded-sm hover:bg-primary/20 transition-all duration-300 cursor-pointer"
            >
              <ExternalLink className="w-6 h-6 text-primary" />
              <div className="text-left flex-1">
                <p className="font-semibold text-primary">{t.download}</p>
                <p className="text-sm text-muted-foreground">{t.downloadSub}</p>
              </div>
            </a>

            <a
              href={termsUrl || '#'}
              target={termsUrl ? "_blank" : "_self"}
              rel="noopener noreferrer"
              data-testid="terms-conditions-link"
              onClick={(e) => {
                if (!termsUrl) {
                  e.preventDefault();
                  alert(language === 'it' 
                    ? 'Link ai Termini e Condizioni non ancora configurato. Contattare l\'amministratore.' 
                    : 'Terms and Conditions link not yet configured. Please contact the administrator.');
                }
              }}
              className="flex items-center gap-3 w-full p-4 bg-secondary/10 border border-secondary/30 rounded-sm hover:bg-secondary/20 transition-all duration-300 cursor-pointer"
            >
              <FileText className="w-6 h-6 text-secondary" />
              <div className="text-left flex-1">
                <p className="font-semibold text-secondary">{t.termsTitle}</p>
                <p className="text-sm text-muted-foreground">{t.termsSub}</p>
              </div>
            </a>

            <div className="p-6 bg-muted/20 border border-white/10 rounded-sm">
              <p className="text-sm leading-relaxed text-muted-foreground">
                {t.consent}
              </p>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <label 
              className="flex items-start gap-4 cursor-pointer group"
              data-testid="privacy-consent-checkbox"
            >
              <Checkbox
                checked={privacyAccepted}
                onCheckedChange={setPrivacyAccepted}
                className="mt-1 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-base leading-relaxed group-hover:text-primary transition-colors">
                {t.checkboxPrivacy}
              </span>
            </label>

            <label 
              className="flex items-start gap-4 cursor-pointer group"
              data-testid="terms-consent-checkbox"
            >
              <Checkbox
                checked={termsAccepted}
                onCheckedChange={setTermsAccepted}
                className="mt-1 data-[state=checked]:bg-secondary data-[state=checked]:border-secondary"
              />
              <span className="text-base leading-relaxed group-hover:text-secondary transition-colors">
                {t.checkboxTerms}
              </span>
            </label>
          </div>

          <Button
            onClick={handleContinue}
            disabled={!privacyAccepted || !termsAccepted}
            data-testid="accept-privacy-button"
            className="w-full h-14 rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <CheckSquare className="w-5 h-5 mr-2" />
            {t.button}
          </Button>
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Smart Info S.r.l. - Cyber Security Services
        </p>
      </div>
    </div>
  );
};

export default PrivacyConsentPage;
